import type {
  RazorpayCheckoutOrder,
  RazorpayCheckoutProof,
  RazorpaySubscriptionCheckoutOrder,
} from '@/types/payment.types'

const CHECKOUT_SCRIPT_ID = 'razorpay-checkout-script'
const CHECKOUT_SCRIPT_URL = 'https://checkout.razorpay.com/v1/checkout.js'

interface RazorpaySuccessResponse {
  razorpay_order_id: string
  razorpay_payment_id: string
  razorpay_signature: string
}

interface RazorpayFailureResponse {
  error?: { description?: string }
}

interface RazorpayInstance {
  open(): void
  on(event: 'payment.failed', handler: (response: RazorpayFailureResponse) => void): void
}

interface RazorpayConstructor {
  new (options: Record<string, unknown>): RazorpayInstance
}

declare global {
  interface Window {
    Razorpay?: RazorpayConstructor
  }
}

function loadCheckoutScript(): Promise<void> {
  if (window.Razorpay) return Promise.resolve()

  return new Promise((resolve, reject) => {
    const existing = document.getElementById(CHECKOUT_SCRIPT_ID) as HTMLScriptElement | null
    const script = existing ?? document.createElement('script')
    const loaded = () => window.Razorpay
      ? resolve()
      : reject(new Error('Razorpay Checkout did not initialize'))
    const failed = () => reject(new Error('Unable to load Razorpay Checkout'))

    script.addEventListener('load', loaded, { once: true })
    script.addEventListener('error', failed, { once: true })
    if (!existing) {
      script.id = CHECKOUT_SCRIPT_ID
      script.src = CHECKOUT_SCRIPT_URL
      script.async = true
      document.head.appendChild(script)
    }
  })
}

/** Open provider-hosted checkout. Null means dismissed, never failed. */
export async function openRazorpayCheckout(
  order: RazorpayCheckoutOrder | RazorpaySubscriptionCheckoutOrder
): Promise<RazorpayCheckoutProof | null> {
  if (order.keyId === 'rzp_test_taxhive_mock') {
    return {
      providerOrderId: order.providerOrderId,
      providerPaymentId: `pay_mock_${order.orderRecordId}`,
      signature: `mock_signature_${order.providerOrderId}`.padEnd(40, '0'),
    }
  }

  await loadCheckoutScript()
  return new Promise((resolve, reject) => {
    let settled = false
    const settle = (value: RazorpayCheckoutProof | null) => {
      if (settled) return
      settled = true
      resolve(value)
    }
    const checkout = new window.Razorpay!({
      key: order.keyId,
      amount: order.amountMinor,
      currency: order.currency,
      name: 'Coding Monkey Labs - TAX HIVE',
      description: 'payment' in order
        ? order.payment.description ?? 'Client payment'
        : 'Tax Hive subscription',
      order_id: order.providerOrderId,
      handler: (response: RazorpaySuccessResponse) => settle({
        providerOrderId: response.razorpay_order_id,
        providerPaymentId: response.razorpay_payment_id,
        signature: response.razorpay_signature,
      }),
      modal: { ondismiss: () => settle(null) },
      retry: { enabled: false },
      theme: { color: '#0f766e' },
    })
    checkout.on('payment.failed', (response) => {
      if (settled) return
      settled = true
      reject(new Error(response.error?.description ?? 'Razorpay payment failed'))
    })
    checkout.open()
  })
}
